import { computed, inject, Injectable, signal } from '@angular/core';
import { signalMethod } from '@ngrx/signals';
import { AuthDataService } from 'src/data-store/auth-data.service';
import { ILogoutOptions, ILogoutInput } from 'src/models/auth.model';
import { ILoggedUser } from 'src/models/user.model';
import { LoggerService } from './logger.service';
import { parseToError } from 'src/util/functions-angular';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { UserDataService } from 'src/data-store/user-data.service';
import { DeviceService } from './device.service';
import { NotificationService } from './notification.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { UserTypes } from 'src/models/common.model';
import { AuthRoute } from 'src/models/routes.model';
import { ConfigService } from './json-config.service';
import { PosthogService } from './posthog.service';
import { InAppBrowserService } from './in-app-browser.service';
import { InAppBrowserAuthService } from './in-app-browser-auth.service';
import { Router } from '@angular/router';
import { PushNotificationsService } from './push-notifications.service';

@Injectable({
  providedIn: 'root'
})
export class LogoutService {
  private authDataService = inject(AuthDataService);
  private loggerService = inject(LoggerService);
  private configService = inject(ConfigService);
  private posthogService = inject(PosthogService);
  private inAppBrowserService = inject(InAppBrowserService);
  private userDataService = inject(UserDataService);
  private inAppBrowserAuthService = inject(InAppBrowserAuthService);
  private deviceService = inject(DeviceService);
  private notificationService = inject(NotificationService);
  private translateService = inject(TranslateService);
  private pushNotificationsService = inject(PushNotificationsService);

  private router = inject(Router);
  private logoutOptions = signal<ILogoutOptions | null>(null);
  private _localAppUrl = '';

  public get localAppUrl(): string {
    const origin = window?.location?.origin;
    if (origin) {
      this._localAppUrl = `${origin}/${AuthRoute.LOGIN}`;
    }

    return this._localAppUrl;
  }

  private browserClose$: null | Subscription = null;
  private urlChange$: null | Subscription = null;

  private logoutSucceededParams = computed(() => ({
    logoutIdToken: this.authDataService.logoutIdToken(),
    loggedUser: this.userDataService.loggedUser(),
    options: this.logoutOptions() ?? {},
  }));

  private logoutFailedParams = computed(() => {
    const authError = this.authDataService.authGetError(ELoadingEntity.AUTH_LOGOUT_ID_TOKEN);
    return {
      error: authError?.error ?? null,
      errorEntity: authError ? ELoadingEntity.AUTH_LOGOUT_ID_TOKEN : null,
      options: this.logoutOptions() ?? {},
    };
  });

  public constructor() {
    this.logoutSucceededEffect(this.logoutSucceededParams);
    this.logoutFailedEffect(this.logoutFailedParams);
  }

  public logOut(options?: ILogoutOptions): void {
    if (this.deviceService.isNativePlatform && this.userDataService.loggedUser()) {
      this.pushNotificationsService.deletePushNotificationsToken();
    }
    const accessToken = this.authDataService.accessToken();
    if (options) {
      this.logoutOptions.set(options);
    }
    this.authDataService.logoutAndGetId(accessToken);
  }

  public readonly logoutSucceededEffect = signalMethod<{
    logoutIdToken: string | null;
    loggedUser: ILoggedUser | null;
    options: ILogoutOptions;
  }>(({ logoutIdToken, loggedUser, options }) => {
    if (logoutIdToken && loggedUser) {
      this.userDataService.resetLoggedUser();
    }
    if (logoutIdToken && !loggedUser) {
      this.finishLogout({
        idleLogoutMessage: !!options?.withMessage,
        userType: options?.userType,
        idToken: logoutIdToken,
      });
    }
  });

  public readonly logoutFailedEffect = signalMethod<{
    error: unknown;
    errorEntity: ELoadingEntity | null;
    options: ILogoutOptions;
  }>(({ error, errorEntity, options }) => {
    if (error && errorEntity === ELoadingEntity.AUTH_LOGOUT_ID_TOKEN) {
      const parsedError = parseToError(error);

      if(parsedError && parsedError.status !== 401 && parsedError.status !== 403) {
        this.loggerService.error('Error during logout process: ', error);
      }
      this.finishLogout({idleLogoutMessage: !!options?.withMessage});
    }
  });

  public finishLogout(options?: ILogoutInput): void {
    //https://gitlab.com/dfcz/bwm-frontend/-/issues/44
    /*if(this.userNotificationService.bellSubscription) {
      this.userNotificationService.bellSubscription.unsubscribe();
    }*/

    this.posthogService.endLog();

    let keycloakLogoutUrl = `${this.configService.getConfig(EnvironmentKeys.oauthUrl)}/logout?client_id=${this.configService.getConfig(EnvironmentKeys.clientId)}`;

    // params for KeyCloak
    if(options?.idToken) {
      keycloakLogoutUrl = keycloakLogoutUrl + '&id_token_hint=' + options.idToken;
    }

    keycloakLogoutUrl += '&post_logout_redirect_uri=' + this.localAppUrl;

    // params for BWM on route back
    if(options) {
      let paramSeparator = '?';
      if(options.displayErrorOnRedirectBack) {
        keycloakLogoutUrl += paramSeparator + 'showNoBwmError=' + (options.userType ?? options?.displayErrorOnRedirectBack ?? 'client') as UserTypes;
        paramSeparator = '&';
      }
      if(options.idleLogoutMessage) {
        keycloakLogoutUrl += paramSeparator + 'idleLogout=true';
        paramSeparator = '&';
      }
    }

    this.authDataService.resetAuthState();
    this.userDataService.resetUserState();

    if (!this.deviceService.isNativePlatform) {
      window.location.href = keycloakLogoutUrl;
    } else {
      this.inAppBrowserService.clearSession();
      this.handleNativeBrowser(keycloakLogoutUrl);
    }
  }

  private async handleNativeBrowser(keycloakLogoutUrl: string): Promise<void> {
    await this.inAppBrowserService.open(keycloakLogoutUrl);

    if(!this.inAppBrowserService.networkConnected) {
      this.inAppBrowserService.close();
      this.notificationService.error(
        this.translateService.instant('toastNotifications.headers.error'),
        this.translateService.instant('toastNotifications.messages.browserLogoutLoadError'),
      );
      return;
    }

    this.urlChange$ = this.getOnUrlChangeStream();
  }

  private getOnUrlChangeStream(): Subscription {
    return this.inAppBrowserService.onUrlChange().subscribe((navUrl) => {
      if (navUrl.startsWith(this.localAppUrl)) {
        this.inAppBrowserService.close();
        this.unsubBrowser();
        this.inAppBrowserAuthService.openInAppBrowser();
      } else {
        this.inAppBrowserService.close();
        this.unsubBrowser();
      }
      this.router.navigate([`/${AuthRoute.LOGIN}`], { replaceUrl: true });
    });
  }

  private unsubBrowser(): void {
    this.browserClose$?.unsubscribe();
    this.urlChange$?.unsubscribe();
    this.browserClose$ = null;
    this.urlChange$ = null;
  }

}